import { isRef, toValue, useFetch } from '#imports'
import { defu } from 'defu'
import type { UseFetchOptions } from 'nuxt/app'

export function useCustomFetch<T>(
  url: MaybeRef<string> | (() => string),
  options: UseFetchOptions<T> = {},
) {
  const runtimeConfig = useRuntimeConfig()
  const { wsUrl } = runtimeConfig.public

  if (!wsUrl) {
    throw new Error(
      `API base URL (${wsUrl}) is not defined,
      check the environment variable named NUXT_PUBLIC_WS_URL`,
    )
  }
  const { token, authenticated, getAuthHeader, isTemp } = useTempAuth()

  const rawURL = toValue(url)

  // URL without /secure prefix if user is not authenticated
  const formattedURL =
    rawURL.startsWith('/secure') && !authenticated.value
      ? rawURL.replace('/secure', '')
      : rawURL

  const authHeader = getAuthHeader({
    authenticated,
    isTemp,
    token,
    url: formattedURL,
  })

  const defaults: UseFetchOptions<T> = {
    baseURL: wsUrl,

    // set user token if authenticated
    headers: authHeader,
    // One asyncData entry per identity. Nuxt keeps the first handler registered under a key (3.17+): with
    // the default key (call site + URL + options), a /secure/… call made after a logout and a new login
    // reused the handler, and the headers, of the first call, so the old, revoked token went out and the
    // API answered 401 (empty ranking, challenges and notification count after a sign-in without reload,
    // ~10 % of the /secure/ranking/top calls on 19/10/2026). The token is part of the key: a new session
    // is a new entry, a visitor and a Waper never share one.
    key: fetchKey(formattedURL, options, authHeader?.Authorization),
  }

  const params = defu(options, defaults)
  // the caller's hooks first, then ours (ofetch runs an array of hooks in order)
  params.onResponse = [
    ...(Array.isArray(options.onResponse)
      ? options.onResponse
      : options.onResponse
        ? [options.onResponse]
        : []),
    refreshNotificationCount(formattedURL, toValue(options.method) ?? 'GET'),
  ]

  return useFetch(formattedURL, params)
}

/**
 * The bell reads its unread count again after an action of the Waper: a like, a comment, a follow, a profile update…
 * can create a notification at once (a "new badge" one, PointService::rewardBadges) or delete one (#1272, #1276),
 * and the count used to wait for the next page (decision of Julien, 01/11/2026: the bell live, no reward modal for
 * these badges). Any successful write to /secure (the notification endpoints aside: they send the count back
 * themselves), and any answer carrying `badges_reward`.
 */
const refreshNotificationCount =
  (url: string, method: string) =>
  ({ response }: { response: { ok?: boolean; _data?: unknown } }) => {
    if (!import.meta.client || response.ok === false) return
    const data = response._data as { badges_reward?: unknown[] } | null
    const write =
      method.toUpperCase() !== 'GET' &&
      url.startsWith('/secure/') &&
      !url.startsWith('/secure/notification')
    if (!write && !data?.badges_reward?.length) return
    useNotificationsStore().fetchUnreadCount()
  }

/** a stable key from what makes the request: URL, method, query, body, and who sends it */
const fetchKey = <T>(
  url: string,
  options: UseFetchOptions<T>,
  authorization?: string,
): string => {
  const method = toValue(options.method) ?? 'GET'
  const who = authorization ? fnv1a(authorization) : 'anon'
  let parts: string
  try {
    parts = JSON.stringify(
      [
        url,
        method,
        toValue(options.query) ?? toValue(options.params) ?? null,
        toValue(options.body) ?? null,
      ],
      // a ref or a computed inside the query or the body (RankingModal passes `month: currentMonth`) is not
      // serializable: its reactivity internals are circular. Use its value, as useFetch does.
      (_key, value) => (isRef(value) ? toValue(value) : value),
    )
  } catch {
    // whatever cannot be serialized (a File, a cycle): the key is the URL and the method only
    parts = JSON.stringify([url, method])
  }

  return `wap:${fnv1a(parts)}:${who}`
}

/** FNV-1a, 32 bits, as a short hexadecimal string: same result on the server and in the browser */
const fnv1a = (input: string): string => {
  let hash = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash.toString(16)
}
